import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  ArrowRight,
  Shield,
  Zap,
  Mail,
  Clock
} from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <Helmet>
        <title>Get Your Custom Quote | Virewire</title>
        <meta name="description" content="Get instant custom quotes for our services. No public pricing - just fair, transparent quotes for your needs." />
      </Helmet>

      <section className="section-padding bg-background">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4 flex items-center justify-center gap-2">
              <DollarSign className="h-12 w-12" />
              Custom Pricing for Your Needs
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We don't believe in one-size-fits-all pricing. Every project is unique, so we create custom quotes tailored to your specific requirements.
            </p>
          </div>

          {/* Main CTA Section */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-12 mb-16 text-center border border-primary/20">
            <h2 className="text-3xl font-bold mb-6">Get Your Instant Quote Today</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Answer a few simple questions about your project, and we'll calculate your custom quote instantly.
            </p>
            <Button 
              size="lg"
              className="gap-2 group"
              onClick={() => navigate('/dashboard/request-service')}
            >
              Get Started <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">How Our Pricing Works</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="text-center">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <Zap className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">1. Create Quote</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Use our quote wizard to specify your service details and requirements
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <Zap className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">2. Instant Calculation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    We calculate your custom price based on your specific needs and requirements
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <Mail className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">3. Email Quote</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Receive your quote via email with a secure link to review and accept
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">4. Secure Payment</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Pay through your personalized payment link whenever you're ready
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Why Custom Quotes?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-l-4 border-l-primary">
                <CardHeader>
                  <CardTitle className="text-xl">Fair Pricing</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    You only pay for exactly what you need. No hidden fees, no surprise charges.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardHeader>
                  <CardTitle className="text-xl">Tailored to You</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Every quote is customized for your property type, location, and specific requirements.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardHeader>
                  <CardTitle className="text-xl">Flexible & Transparent</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Review your quote, see the breakdown, and make an informed decision at your own pace.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  How long does a quote take?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Our quote engine calculates your custom price instantly. You'll have your quote within seconds of completing the form.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  What if I need to change my quote?
                </h3>
                <p className="text-sm text-muted-foreground">
                  You can create a new quote anytime with different specifications. Each quote is valid for 24 hours.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Is my quote secure?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Yes. You receive a secure email link that only you can access. Your quote is protected and expires after 24 hours.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  Are there discounts for bulk orders?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Absolutely! Our quote system accounts for volume and frequency. Get your quote to see any applicable discounts.
                </p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-primary/10 rounded-lg p-12 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Ready to Get Your Custom Quote?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              It only takes a few minutes to get an instant, customized quote for your project.
            </p>
            <Button 
              size="lg"
              className="gap-2 group"
              onClick={() => navigate('/dashboard/request-service')}
            >
              Create Your Quote Now <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PricingPage;
